// loginhandler.ts ↔ game/loginhandler.cpp — the login state machine the MP
// shell flow shares: username/password/token + anonymous, the transport Login
// (async here — the C++ blocks in WaitForStateChange), and GetToken, the
// blocking HTTPS auth request (game/simplerequest.cpp) over an injected
// httpGet seam.
//
// The C++ globals (Multiplayer.cpp gszUsername/gszPassword/gszToken/
// gfAnonymous backed by Game::SavePreferences) ride gpprefs (io/prefs.ts) —
// InitFromGlobals reads the prefs, SaveToGlobals writes them back and saves.

import {
  knLoginResultSuccess,
  knLoginResultAnonymousSuccess,
  knLoginResultFail,
} from '../net/xtransport.ts';
import type { MpGlobals } from './multiplayer.ts';
import type { Preferences } from '../io/prefs.ts';
import { kszPrefUsername, kszPrefPassword, kszPrefToken, kfPrefAnonymous } from '../io/prefs.ts';

// mpshared/messages.h:78-82 — the extended login results (the base codes live
// in net/xtransport.ts)
export const knLoginResultStaleToken = 3;
export const knLoginResultAuthDown = 4;
export const knLoginResultNoPassword = 5;
export const knLoginResultNoAnons = 6;
export const knLoginResultAccountInUse = 7;

// mpshared/mpht.h:47-50
export const kcbPlayerName = 32;
export const kcbPassword = 32;
export const kcbTokenMax = 256;

// game/loginhandler.cpp:11-15
export const knGetTokenResultSuccess = 0;
export const knGetTokenResultNoPassword = 1;
export const knGetTokenResultTokenInvalid = 2;
export const knGetTokenResultCreateRequestError = 3;
export const knGetTokenResultServiceUnavailable = 4;

// game/serviceurls.cpp:20 (the production block)
export const kszAuthUrl = 'https://api.warfareincorporated.com/api/auth';

/** The host/global reaches: SimpleRequest (an HTTP GET returning the body),
 *  gszDeviceId, HostGetPlatformString, ggame.SavePreferences. */
export interface LoginHandlerDeps {
  mp: MpGlobals; // gptra
  prefs: Preferences; // the gszUsername/... backing store
  savePreferences(): void; // ggame.SavePreferences
  /** SimpleRequest::Get — resolves the response body, or null on transport
   *  failure (the C++ false return). */
  httpGet(url: string): Promise<string | null>;
  deviceId: string; // gszDeviceId
  platform: string; // HostGetPlatformString()
  authUrl?: string; // kszAuthUrl (tests/local stack override)
}

// base::base64encode alphabet (base/base64.cpp — standard RFC 4648)
function base64(bytes: Uint8Array): string {
  const A = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const b2 = i + 2 < bytes.length ? bytes[i + 2] : 0;
    out += A[b0 >> 2] + A[((b0 & 3) << 4) | (b1 >> 4)];
    out += i + 1 < bytes.length ? A[((b1 & 15) << 2) | (b2 >> 6)] : '=';
    out += i + 2 < bytes.length ? A[b2 & 63] : '=';
  }
  return out;
}

// base::StringEncoder::QueryEncode
export const queryEncode = (s: string): string => encodeURIComponent(s);

export class LoginHandler {
  private readonly deps: LoginHandlerDeps;
  private loggedin_ = false;
  private anonymous_ = false;
  private username_ = '';
  private password_ = '';
  private token_ = '';

  // loginhandler.cpp:17-19
  constructor(deps: LoginHandlerDeps) {
    this.deps = deps;
    this.InitFromGlobals();
  }

  loggedin(): boolean {
    return this.loggedin_;
  }
  anonymous(): boolean {
    return this.anonymous_;
  }
  username(): string {
    return this.username_;
  }
  password(): string {
    return this.password_;
  }
  token(): string {
    return this.token_;
  }

  // loginhandler.cpp:24-33
  private InitFromGlobals(): void {
    this.token_ = this.deps.prefs.GetString(kszPrefToken);
    this.password_ = this.deps.prefs.GetString(kszPrefPassword);
    this.username_ = this.deps.prefs.GetString(kszPrefUsername);
    // #if 1 — Always show non-anonymous login (loginhandler.cpp:28-32)
    this.SetAnonymous(false);
  }

  // loginhandler.cpp:35-41
  private SaveToGlobals(): void {
    this.deps.prefs.SetString(kszPrefToken, this.token_);
    this.deps.prefs.SetString(kszPrefPassword, this.password_);
    this.deps.prefs.SetString(kszPrefUsername, this.username_);
    this.deps.prefs.SetBool(kfPrefAnonymous, this.anonymous_);
    this.deps.savePreferences();
  }

  // loginhandler.cpp:43-53
  SetAnonymous(anonymous: boolean): void {
    this.anonymous_ = anonymous;
  }
  SetUsername(username: string): void {
    this.username_ = username.slice(0, kcbPlayerName - 1);
  }
  SetPassword(password: string): void {
    this.password_ = password.slice(0, kcbPassword - 1);
  }

  // loginhandler.cpp:55-65
  GetPlayerName(): string {
    if (this.anonymous_) {
      const tra = this.deps.mp.tra;
      return tra !== null ? tra.GetAnonymousUsername() : 'anonymous';
    }
    return this.username_;
  }

  // loginhandler.cpp:67-139 — the C++ blocks; here async. The double-GetToken
  // bug at :105-106 (`if (!GetToken())` then calling GetToken() AGAIN inside
  // the switch) is preserved branch-faithfully below.
  async Login(): Promise<number> {
    const tra = this.deps.mp.tra;
    if (tra === null) return knLoginResultFail;

    if (!this.anonymous_ && this.token_ === '') {
      switch (await this.GetToken()) {
        case knGetTokenResultSuccess:
          break;
        case knGetTokenResultNoPassword:
          return knLoginResultNoPassword;
        case knGetTokenResultTokenInvalid:
        case knGetTokenResultCreateRequestError:
          return knLoginResultFail;
        case knGetTokenResultServiceUnavailable:
          return knLoginResultAuthDown;
      }
    }

    // Anonymous login MUST send an EMPTY token — the server keys anonymity off an empty token
    // (workers/multiplayer tokenauth.ts isAnonymous), assigning an `anon<id>` name. Sending the STALE
    // saved token + username (left over from a prior NON-anonymous login, loaded by InitFromGlobals) made
    // the server authenticate the real account instead, leaking the real username into the roster, the
    // replay, and chat. Send an empty username too so the real name is never transmitted on an anon session.
    const loginName = this.anonymous_ ? '' : this.username_;
    const loginToken = this.anonymous_ ? '' : this.token_;
    let result = await tra.Login(loginName, loginToken);

    // If successful, return and keep the token
    if (result === knLoginResultSuccess || result === knLoginResultAnonymousSuccess) {
      this.loggedin_ = true;
      this.SaveToGlobals();
      return result;
    }

    // Otherwise don't save the token
    this.token_ = '';

    // If token stale, try again otherwise return
    if (result !== knLoginResultStaleToken) {
      return result;
    }

    // Get a new token. PRESERVED BUG (loginhandler.cpp:105-117): the C++
    // tests `if (!GetToken())` — GetToken() returns 0 (knGetTokenResultSuccess)
    // on SUCCESS, so the `if` body runs on SUCCESS and calls GetToken() AGAIN
    // (a second auth round-trip), while a FAILURE skips the switch entirely
    // and proceeds to Login with an empty token (which then fails).
    if ((await this.GetToken()) === knGetTokenResultSuccess) {
      switch (await this.GetToken()) {
        case knGetTokenResultSuccess:
          break;
        case knGetTokenResultNoPassword:
          return knLoginResultNoPassword;
        case knGetTokenResultTokenInvalid:
        case knGetTokenResultCreateRequestError:
          return knLoginResultFail;
        case knGetTokenResultServiceUnavailable:
          return knLoginResultAuthDown;
      }
    }

    result = await tra.Login(this.username_, this.token_);

    if (result === knLoginResultSuccess || result === knLoginResultAnonymousSuccess) {
      this.loggedin_ = true;
      this.SaveToGlobals();
      return result;
    }

    this.token_ = '';

    // If this token is stale for some reason, there is a bug. Convert into "fail".
    if (result === knLoginResultStaleToken) {
      return knLoginResultFail;
    }

    return result;
  }

  // loginhandler.cpp:141-154
  async SignOut(): Promise<number> {
    this.loggedin_ = false;
    this.token_ = '';

    if (!this.anonymous_) {
      // Sign out user - erase password, keep username
      this.password_ = '';
    }
    this.SaveToGlobals();
    const tra = this.deps.mp.tra;
    if (tra === null) return 1; // knSignOutResultFail
    return tra.SignOut();
  }

  // loginhandler.cpp:156-217
  async GetToken(): Promise<number> {
    // Wipe out the current token, if there is one
    this.token_ = '';

    // Don't send a request for a token if the user has no password
    if (this.password_ === '') {
      return knGetTokenResultNoPassword;
    }

    // Ask the authentication server for an auth token. Use https so ok to
    // send password cleartext. Encode base64 to avoid url encoding parameters.
    // (loginhandler.cpp:169-180 — "user#pass" with the '#' replaced by NUL,
    // then base64 over user ‖ 0x00 ‖ pass.)
    const enc = new TextEncoder();
    const u = enc.encode(this.username_);
    const p = enc.encode(this.password_);
    const input = new Uint8Array(u.length + 1 + p.length);
    input.set(u, 0);
    input[u.length] = 0;
    input.set(p, u.length + 1);
    const output = base64(input);

    // Submit a simple blocking request to get the token
    const d = queryEncode(this.deps.deviceId);
    const o = queryEncode(this.deps.platform);
    const url = `${this.deps.authUrl ?? kszAuthUrl}?a=${queryEncode(output)}&d=${d}&o=${o}`;

    const result = await this.deps.httpGet(url);
    if (result === null) {
      return knGetTokenResultServiceUnavailable;
    }

    // Strip whitespace from the start and end (loginhandler.cpp:197-215)
    const token = result.trim();
    if (token.length === 0) {
      return knGetTokenResultTokenInvalid;
    }
    this.token_ = token.slice(0, kcbTokenMax - 1);
    return knGetTokenResultSuccess;
  }

  // loginhandler.cpp:219-224
  ShouldAttemptLogin(): boolean {
    if (this.anonymous_) return true;
    return this.username_ !== '' && this.password_ !== '';
  }

  // loginhandler.cpp:226-237
  StatsUsername(): string {
    // Only return username if: 1. not anonymous, 2. it has been used to
    // login successfully before
    if (this.anonymous_) return '';
    if (this.token_ === '') return '';
    return this.username_;
  }
}
